import { ServerConfig } from "@/config/config";
import { Sale, SaleRow } from "@/models/base";
import SaleRowExtraFields from "@/models/base/sale-row-extra-fields";
import { Job } from "@/models/job";
import { View } from "@/models/view";
import { Axios } from "@/utils/axios";
import { advancedSearch, advancedSearchStaticView } from "@/utils/browse/browse";
import { IGetResponse } from "@/utils/get-response";
import { HumanFilter } from "@/utils/human-filter";
import { IExtraFieldQuickEditViaMenuDataResult } from "@/utils/models/extra-fields";
import { getModel } from "@/utils/models/model";
import { IQueryParams, IQueryParamsWithOptionalView } from "@/utils/query-params";
import { FetchedTotal } from "@/utils/views/fetched-total";

export interface IMarkAsDeliveredResponse {
	changedRows:number;
	undeliverableCount:number;
}

export class SaleRowServiceClass{
	private isForPurchaseDocuments:boolean = false;
	public constructor(isPurchaseRow:boolean) {
		this.isForPurchaseDocuments = isPurchaseRow;
	}

	get url():string{
		if (this.isForPurchaseDocuments) {
			return ServerConfig.host + "/purchase-document-row";
		}
		return ServerConfig.host + "/sale-row";
	}

	async getSalesRows(query: IQueryParamsWithOptionalView, extraFilters:HumanFilter[] = [], bookyear: string = ""): Promise<IGetResponse<SaleRow>> {
		let result = await advancedSearch(query, this.url, getModel("SaleRow"), extraFilters,{Bookyear: bookyear});
		let records = result.data.records as number;
		let sales = result.data.data.map((c: any) => new SaleRow(c));
		return {data: sales, records};
	}

	async markAsDelivered(rows:SaleRow[], amount:number = 0, date: Date = new Date()):Promise<IMarkAsDeliveredResponse>{
		let body = rows.map(r=>{
			return {SaleRowID: r.ID, Amount: amount || r.Backorder, Date: date};
		});
		let result = await Axios.post(`${this.url}/mark-as-delivered`, body);
		return {
			changedRows: result.data.ChangedRows,
			undeliverableCount: result.data.UndeliverableRows,
		};
	}

	async markAsUndeliverable(rows:SaleRow[]):Promise<SaleRow[]>{
		let result = await Axios.post(`${this.url}/mark-as-undeliverable`, rows.map(r=>r.ID));
		return (result.data || []).map((r:any)=>new SaleRow(r));
	}
	async markAsDeliverable(rows:SaleRow[]):Promise<SaleRow[]>{
		let result = await Axios.post(`${this.url}/mark-as-deliverable`, rows.map(r=>r.ID));
		return (result.data || []).map((r:any)=>new SaleRow(r));
	}

	async markAsValidated(rows:SaleRow[]):Promise<SaleRow[]>{
		let result = await Axios.post(`${this.url}/mark-as-validated`, rows.map(r=>r.ID));
		return (result.data || []).map((r:any)=>new SaleRow(r));
	}

	async getTotals(view:View):Promise<FetchedTotal[]>{
		let result = await Axios.get(`${this.url}/view/${view.ID}/totals`);
		return (result.data || []).map((d:any)=>new FetchedTotal(d));
	}

	async getSaleRowsStaticView(view:View, query: IQueryParams, bookyear: string = "", extraFilters:HumanFilter[] = []):Promise<IGetResponse<Sale>> {
		let result = await advancedSearchStaticView(view, query, this.url + "/raw-view", extraFilters, {Bookyear: bookyear});
		let records = result.data.records as number;
		let sales = result.data.data.map((c: any) => new SaleRow(c));
		return {data: sales, records};
	}
	async createAfleveringsbonnenOfDeliveredGoods(rowIds:number[], targetDocumentTypeId:number):Promise<Job>{
		let result = await Axios.post(`${this.url}/create-afleveringsbonnen-of-delivered-goods`, {
			SaleRowIDs: rowIds,
			TargetDocTypeID: targetDocumentTypeId
		});
		return new Job(result.data);
	}

	async afleveringsbonnenOfDeliveredGoodsCount():Promise<number>{
		let result = await Axios.get(`${this.url}/afleveringsbonnen-of-delivered-goods-count`);
		return result.data.Count;
	}


	public async updateExtraFields(row:SaleRow, extraFields:SaleRowExtraFields):Promise<SaleRowExtraFields>{
		let result = await Axios.put(`${this.url}/extra-fields/${row.ID}`, extraFields.getJSON());
		return new SaleRowExtraFields(result.data);
	}

	public async batchEditExtraField(row:SaleRow[], field:string, value:IExtraFieldQuickEditViaMenuDataResult) {
		let body = {
			IDs: row.map(s=>s.ID),
			Field: field,
			NumValue: value.numValue,
			TextValue: value.textValue,
			BoolValue: value.boolValue,
			TimeValue: value.timeValue
		};
		await Axios.post(`${this.url}/batch-edit-extra-field`, body);
	}

	public async getExtraFields(row:SaleRow | number):Promise<SaleRowExtraFields>{
		if (typeof(row) == "object") {
			row = row.ID;
		}
		let result = await Axios.get(`${this.url}/${row}/extra-fields`);
		return new SaleRowExtraFields(result.data);
	}
};

export const SaleRowService = new SaleRowServiceClass(false);
export const PurchaseDocumentRowService = new SaleRowServiceClass(true);